import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, ImagePlus, Loader2, Pencil, Plus, Save, Trash2, Users, X } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, FieldRow, PageHead, Section, TONES, Toggle, inputCls } from "@/components/account/Shell";
import { Cell, Filters, IconBtn, PillRow, Sheet, Tag, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";

const TONE = TONES.listings;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const BLANK = { id: null, title: "", description: "", date: "", time: "", length: "", price: 99, seats: 3, image: "", active: true };
const SHOW = [
    { key: "all", label: "Everything", match: () => true },
    { key: "open", label: "Seats left", match: (c) => !c.full && c.active !== false },
    { key: "full", label: "Full", match: (c) => !!c.full },
    { key: "paused", label: "Not taking signups", match: (c) => c.active === false },
];
const COLS = [
    { key: "photo", label: "Photo", w: "60px" },
    { key: "title", label: "Class", sub: "what's on the board", w: "minmax(220px,1.8fr)" },
    { key: "when", label: "When", sub: "date · time · how long", w: "minmax(180px,1.2fr)" },
    { key: "price", label: "Price", sub: "$ per seat", w: "90px" },
    { key: "seats", label: "Seats", sub: "taken / total", w: "130px" },
    { key: "signups", label: "Taking signups", w: "120px" },
    { key: "actions", label: "Actions", sub: "edit · delete", w: "90px" },
];
const SIGNUP_COLS = [
    { key: "name", label: "Who signed up", w: "minmax(180px,1fr)" },
    { key: "contact", label: "Contact", sub: "email · phone", w: "minmax(240px,1.4fr)" },
    { key: "class", label: "Class", w: "minmax(220px,1.4fr)" },
];

export default function StoreClasses() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(BLANK);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [show, setShow] = useState("all");
    const fileRef = useRef(null);

    const load = useCallback(() => {
        api.get("/store/classes").then(({ data }) => setData(data))
            .catch((e) => setData({ error: formatApiError(e.response?.data?.detail) }));
    }, []);
    useEffect(() => { load(); }, [load]);

    const run = async (fn, msg) => {
        setBusy(true);
        try { await fn(); load(); if (msg) toast.success(msg); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const save = () => run(async () => {
        const { data: res } = await api.post("/store/classes", {
            ...form, price: Number(form.price) || 0, seats: Number(form.seats) || 1,
        });
        toast.success(res.note);
        setForm(BLANK); setOpen(false);
    });

    const pickPhoto = async (file) => {
        if (!file) return;
        setBusy(true);
        try { const rec = await uploadArtwork(file); setForm((f) => ({ ...f, image: rec.url })); }
        catch { toast.error("Upload failed"); }
        finally { setBusy(false); }
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const edit = (c) => { setForm({ ...BLANK, ...c }); setOpen(true); };

    if (!data) return <BackOffice><div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div></BackOffice>;

    if (data.error) return (
        <BackOffice>
            <div className="mx-auto max-w-2xl py-16 text-center" data-testid="classes-locked">
                <GraduationCap size={32} className="mx-auto text-zinc-500" />
                <h1 className="mt-3 font-display text-2xl font-black text-zinc-900">Classes come with a space.</h1>
                <p className="mt-2 text-sm text-zinc-700">{data.error}</p>
                <Link to="/account/sell" className="mt-5 inline-block"><Btn tone="solid" testid="classes-open-space">See the spaces</Btn></Link>
            </div>
        </BackOffice>
    );

    const shown = data.classes.filter(SHOW.find((s) => s.key === show).match);

    return (
        <BackOffice>
            <PageHead kicker="Listings" tone={TONE} title="Classes" testid="store-classes-head"
                sub="Classes are shared across the directory and every shop teaches its own. Write one here and it goes straight onto the classes board with your shop's name on it."
                right={
                    <div className="text-right">
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Seats sold / booked</p>
                        <p className="font-mono text-2xl font-black tabular-nums text-zinc-900" data-testid="classes-booked">
                            {data.seats_sold}<span className="text-zinc-600"> · {money(data.booked)}</span>
                        </p>
                        <div className="mt-1.5">
                            <Btn tone="solid" testid="class-new" onClick={() => { setForm(BLANK); setOpen(true); }}>
                                <Plus size={11} /> new class
                            </Btn>
                        </div>
                    </div>
                } />

            <Filters testid="classes-filters">
                <PillRow label="Show" testid="classes-show" value={show} onPick={setShow}
                    choices={SHOW.map((s) => ({ key: s.key, label: s.label, count: data.classes.filter(s.match).length }))} />
            </Filters>

            <div className="mt-3 space-y-3">
                {open && (
                    <Section tone={TONE} pad={false} testid="class-form" title={form.id ? `Edit ${form.title || "class"}` : "Write a class"}
                        note="one field per line · your class shows on the Classes page, listed first for anyone inside your shop"
                        right={<Btn testid="class-cancel" onClick={() => { setOpen(false); setForm(BLANK); }}><X size={11} /> cancel</Btn>}>
                        <FieldRow label="Title" hint="what it's called on the board">
                            <input className={inputCls} placeholder="Custom Garment Printing Workshop" value={form.title} onChange={set("title")} data-testid="class-title" />
                        </FieldRow>
                        <FieldRow label="What they'll learn" hint="a sentence or two" top>
                            <textarea className={inputCls} rows={3} value={form.description} onChange={set("description")}
                                placeholder="Two hours on the press: art prep, heat, pressure, time." data-testid="class-description" />
                        </FieldRow>
                        <FieldRow label="Date">
                            <input type="date" className={`${inputCls} max-w-[220px]`} value={form.date} onChange={set("date")} data-testid="class-date" />
                        </FieldRow>
                        <FieldRow label="Time" hint="as you'd say it — 6:00 PM">
                            <input className={`${inputCls} max-w-[220px]`} placeholder="6:00 PM" value={form.time} onChange={set("time")} data-testid="class-time" />
                        </FieldRow>
                        <FieldRow label="How long" hint="2 hours, a Saturday morning…">
                            <input className={`${inputCls} max-w-[220px]`} placeholder="2 hours" value={form.length} onChange={set("length")} data-testid="class-length" />
                        </FieldRow>
                        <FieldRow label="Price per person" hint="$ per seat">
                            <input type="number" min="0" step="1" className={`${inputCls} max-w-[160px]`} value={form.price} onChange={set("price")} data-testid="class-price" />
                        </FieldRow>
                        <FieldRow label="Seats" hint="how many people fit">
                            <input type="number" min="1" step="1" className={`${inputCls} max-w-[160px]`} value={form.seats} onChange={set("seats")} data-testid="class-seats" />
                        </FieldRow>
                        <FieldRow label="Photo" hint="the picture on the classes board">
                            <div className="flex items-center gap-2">
                                <span className="flex h-10 w-10 items-center justify-center overflow-hidden border border-[#d0d0d0] bg-[#fafafa]">
                                    {form.image ? <img src={mediaUrl(form.image)} alt="" className="h-full w-full object-cover" />
                                        : <GraduationCap size={16} className="text-[#4a4a4a]" />}
                                </span>
                                <Btn onClick={() => fileRef.current?.click()} disabled={busy} testid="class-photo"><ImagePlus size={11} /> photo</Btn>
                                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickPhoto(e.target.files?.[0])} />
                            </div>
                        </FieldRow>
                        <FieldRow label="Taking signups" hint="off = still on the board, no new seats">
                            <Toggle on={!!form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} testid="class-active" />
                        </FieldRow>
                        <div className="border-t border-[#e5e5e5] px-2.5 py-2">
                            <Btn tone="solid" onClick={save} disabled={busy} testid="class-save">
                                {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} {form.id ? "Save class" : "Put it on the board"}
                            </Btn>
                        </div>
                    </Section>
                )}

                <Section tone={TONE} pad={false} testid="classes-list"
                    title={`${shown.length} class${shown.length === 1 ? "" : "es"} on the board`}
                    note="seats fill from the public classes page">
                    <Sheet cols={COLS} rows={shown} rowKey={(c) => c.id} testid="classes-sheet" minWidth={960}
                        rowTestid={(c) => `class-row-${c.id}`}
                        empty={data.classes.length ? "Nothing matches that filter." : "No classes yet. Teach one Saturday morning session and it fills the shop with future customers."}
                        cells={(c) => ({
                            photo: (
                                <Cell>
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden border border-[#d0d0d0] bg-[#fafafa]">
                                        {c.image ? <img src={mediaUrl(c.image)} alt="" className="h-full w-full object-cover" /> : <GraduationCap size={13} className="text-[#4a4a4a]" />}
                                    </span>
                                </Cell>
                            ),
                            title: <Cell><Two a={c.title} b={c.description} /></Cell>,
                            when: <Cell><Txt>{[c.date, c.time, c.length].filter(Boolean).join(" · ") || "no date set"}</Txt></Cell>,
                            price: <Cell><Txt bold mono right>{money(c.price)}</Txt></Cell>,
                            seats: (
                                <Cell>
                                    <Tag status={c.full ? "rejected" : "completed"} testid={`class-seats-${c.id}`}>
                                        <Users size={9} /> {c.taken}/{c.seats}{c.full ? " full" : ` · ${c.left} left`}
                                    </Tag>
                                </Cell>
                            ),
                            signups: <Cell><Tag status={c.active === false ? "removed" : "active"}>{c.active === false ? "paused" : "open"}</Tag></Cell>,
                            actions: (
                                <Cell className="justify-end">
                                    <IconBtn onClick={() => edit(c)} testid={`class-edit-${c.id}`} title="Edit this class"><Pencil size={12} /></IconBtn>
                                    <IconBtn onClick={() => run(() => api.delete(`/store/classes/${c.id}`), "Class removed")} disabled={busy}
                                        testid={`class-delete-${c.id}`} title="Delete this class" danger><Trash2 size={12} /></IconBtn>
                                </Cell>
                            ),
                        })} />
                    {!data.classes.length && (
                        <div className="border-t border-[#d0d0d0] px-3 py-2 text-center" data-testid="classes-empty">
                            <button onClick={() => { setForm(BLANK); setOpen(true); }} data-testid="classes-empty-new"
                                className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-900 hover:underline">
                                write a class →
                            </button>
                        </div>
                    )}
                </Section>

                <Section tone={TONE} pad={false} testid="class-signups" title={`${data.signups.length} signups`}
                    note="call or email them to confirm the seat">
                    <Sheet cols={SIGNUP_COLS} rows={data.signups} rowKey={(s) => s.id} testid="signups-sheet" minWidth={760}
                        rowTestid={(s) => `signup-${s.id}`} empty="Nobody has signed up yet."
                        cells={(s) => ({
                            name: <Cell><Txt bold>{s.name}</Txt></Cell>,
                            contact: <Cell><Txt sub>{[s.email, s.phone].filter(Boolean).join(" · ")}</Txt></Cell>,
                            class: <Cell><Txt>{s.class_title}</Txt></Cell>,
                        })} />
                </Section>
            </div>
        </BackOffice>
    );
}
